import React from 'react';
import styled from 'styled-components';

import { SplitBlockAppearance, SplitBlockImage } from 'api/cms';
import { Box } from 'components/Box';
import { FilestackImage } from 'components/Image';
import { ConditionalLink } from 'components/Link';
import { colors, gutterWidth } from 'theme';

type ImageContainerProps = {
  appearance: SplitBlockAppearance;
  fixedWidth: boolean;
  imageTextBlock: boolean;
};

export const ImageContainer = styled.div<ImageContainerProps>`
  width: 100%;
  height: 100%;
  ${(props) => `
    ${
      !props.fixedWidth
        ? `
      * {
        width: 100%;
        height: 100%;
      }
      svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 40px;
        height: 40px;
      }
      img {
        width: 100%;
        height: auto;
        display: none;
        ${
          props.imageTextBlock &&
          props.appearance !== 'FULL_WIDTH' &&
          'border-radius: 10px;'
        }
      }
      @supports (object-fit: cover) {
        img {
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
        }
      }
    `
        : `
      img {
        width: 100%;
        ${
          props.imageTextBlock &&
          props.appearance !== 'FULL_WIDTH' &&
          'border-radius: 10px;'
        }
      }
      @supports (object-fit: contain) {
        img {
          height: 100%;
          object-fit: contain;
          object-position: center;
          display: block;
        }
      }
        `
    }
  `}
`;

type Props = {
  appearance: SplitBlockAppearance;
  blockId: string;
  section: SplitBlockImage;
  showBackgroundImage: boolean;
  imageTextBlock: boolean;
  index: number;
  preview: boolean;
};

export function ImageContent({
  appearance,
  blockId,
  imageTextBlock,
  index,
  preview,
  section,
  showBackgroundImage,
}: Props) {
  const overlapping = appearance === 'OVERLAPPING';
  const fullWidth = appearance === 'FULL_WIDTH';
  const format = !['image/gif', 'image/jpeg', 'image/png'].includes(
    section.data.image.mimetype
  )
    ? 'jpg'
    : undefined;
  return showBackgroundImage ? null : (
    <Box
      data-qa-id={`split-content-section-${index}`}
      position={[undefined, null, !fullWidth ? 'relative' : undefined]}
      width={[1, null, 1 / 2]}
      px={[imageTextBlock ? '18px' : gutterWidth / 4, null, gutterWidth / 2]}
      py={[
        imageTextBlock ? gutterWidth / 2 : gutterWidth / 4,
        null,
        gutterWidth / 2,
      ]}
      key={blockId}
    >
      <Box
        position={[null, null, fullWidth && !preview ? 'absolute' : undefined]}
        left={[null, null, '50%']}
        top={[null, null, '50%']}
        transform={[
          null,
          null,
          fullWidth && !preview && 'translate(-50%, -50%)',
        ]}
        height={
          section.data.size === 'FIXED_WIDTH'
            ? 'auto'
            : [
                '100%',
                null,
                overlapping || fullWidth
                  ? `calc(100% + ${
                      overlapping ? gutterWidth * 2 : gutterWidth
                    }px)`
                  : '100%',
              ]
        }
        mt={overlapping && [0, null, -gutterWidth]}
        width={
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line no-nested-ternary
          overlapping
            ? [1, null, `calc(100% + ${gutterWidth * 3}px)`]
            : fullWidth
            ? [1, null, '100vw']
            : 1
        }
        ml={[0, null, overlapping && index === 1 && -gutterWidth * 3]}
        overflow="hidden"
        bg={colors.fog}
        borderRadius={!fullWidth ? '24px' : 0}
        style={{
          '-webkit-mask-image': '-webkit-radial-gradient(white, black)',
        }}
      >
        <Box data-qa-id="split-content-section-image">
          <ConditionalLink
            link={section.data.url}
            data-qa-id="split-content-section-image-url"
          >
            <ImageContainer
              appearance={appearance}
              imageTextBlock={imageTextBlock}
              fixedWidth={section.data.size === 'FIXED_WIDTH'}
            >
              <FilestackImage
                data-qa-id="split-content-section-image"
                data={section.data.image}
                alt={
                  (section.data.image && section.data.image.altText) ||
                  (section.data.image && section.data.image.filename)
                }
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line no-nested-ternary
                width={fullWidth ? 1440 : overlapping ? 696 : 552}
                format={format}
              />
            </ImageContainer>
          </ConditionalLink>
        </Box>
      </Box>
    </Box>
  );
}
